import { PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { INVALID_ALL_INPUTS } from 'config'
import { RolodexModal } from 'pages/Loan/Parties/Modal/RolodexModal'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { attachEscrowHoldingBack, generateEscrowHoldingBackPdf, getEscrowHoldingBack, updateEscrowHoldingBack } from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Modal, RadioGroup } from 'stories/components'
import { OverrideCaclModal } from 'stories/components/OverrideCalcModal/OverrideCalcModal'
import { getFullName, InputConvert, InputValidate, removeComma } from 'utils'
import { notifyLoanSubmissionChanged } from 'utils/loanSubmissionSync'
import { RenderInput } from 'utils/RenderInput'

const GENERATE_OPTIONS: Record<string, string> = {
  download: 'Generate Document',
  both: 'Generate Document And Add to Document Storage',
  storage: 'Add To Document Storage Only',
}

const PARTY_FIELDS = ['sellerCompany', 'sellerName', 'lenderCompany', 'lenderName', 'lenderTitle']

const personName = (item: Record<string, any>) =>
  String(item.Name || '').trim() || getFullName(item.FirstName, item.MiddleName, item.LastName)

export default function EscrowHoldbackAgreement() {
  const [loading, setLoading] = useState(true)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const [source, setSource] = useState<Record<string, string>>({})
  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [ovData, setOVData] = useState<any>({})
  const [rolodexFor, setRolodexFor] = useState<'seller' | 'lender' | ''>('')
  const [showGenerate, setShowGenerate] = useState(false)
  const [generateOption, setGenerateOption] = useState('download')

  useEffect(() => {
    getEscrowHoldingBack()
      .then(({ inputs, data }) => {
        const nextSource: Record<string, string> = {}
        const nextOv: Record<string, string> = {}
        PARTY_FIELDS.forEach((key) => {
          nextSource[key] = data[key] || ''
          nextOv[key] = data[`${key}OV`] || ''
        })
        Object.keys(inputs).forEach((key) => {
          inputs[key].value = data[key]
        })
        setSource(nextSource)
        setOverrides(nextOv)
        setInputs(inputs)
      })
      .finally(() => setLoading(false))
  }, [])

  const onChange = (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onBlur = async (key: string) => {
    if (PARTY_FIELDS.includes(key)) return
    let value = InputConvert(inputs[key], inputs[key].value)
    if (inputs[key].type == 'thousandSep') value = removeComma(value)
    setLoading(true)
    await updateEscrowHoldingBack({ [key]: value })
    setLoading(false)
  }

  const saveOverrides = async (patch: Record<string, string>) => {
    const next = { ...overrides }
    Object.entries(patch).forEach(([key, value]) => {
      next[key.replace(/OV$/, '')] = value
    })
    setOverrides(next)
    setLoading(true)
    await updateEscrowHoldingBack(patch)
    setLoading(false)
  }

  const closeOVModal = async (save: boolean, modalData: any) => {
    if (save) {
      const key = ovData.overrideKey as string
      const raw = modalData?.ovValue
      await saveOverrides({ [`${key}OV`]: raw === undefined || raw === null ? '' : String(raw) })
    }
    setOVData({})
  }

  const onCloseRolodex = async (item: Record<string, any> | null) => {
    const target = rolodexFor
    setRolodexFor('')
    if (!item || !target) return
    const company = item.Company || ''
    const name = personName(item)
    const patch: Record<string, string> =
      target === 'seller'
        ? { sellerCompanyOV: company, sellerNameOV: name }
        : { lenderCompanyOV: company, lenderNameOV: name, lenderTitleOV: String(item.Title || '') }
    await saveOverrides(patch)
  }

  const rolodexButton = (target: 'seller' | 'lender') => (
    <button
      type="button"
      className="not-italic text-xs text-shade-blue hover:underline"
      onClick={() => setRolodexFor(target)}
    >
      Pull from Rolodex
    </button>
  )

  const formHasError = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    for (const key in newStats) {
      const error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
    }
    if (hasError) {
      setInputs(newStats)
      toast(INVALID_ALL_INPUTS, { type: 'error' })
    }
    return hasError
  }

  const onGenerate = () => {
    if (formHasError()) return
    setGenerateOption('download')
    setShowGenerate(true)
  }

  const onConfirmGenerate = async () => {
    const option = generateOption
    setShowGenerate(false)
    setLoading(true)
    try {
      if (option === 'download') await generateEscrowHoldingBackPdf()
      else {
        await attachEscrowHoldingBack(option === 'both')
        notifyLoanSubmissionChanged()
        toast(option === 'both' ? 'Generated and added to Document Storage' : 'Added to Document Storage', {
          type: 'success',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`ReviewForm-container`}>
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white`}>
          <LayoutLoading show={loading} />
          <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
            <span>Escrow Holdback Agreement</span>
            {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
          </h2>
          <div className="review-form-container overflow-show">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-5">
              {Object.keys(inputs).map((key, index) => {
                const input = { ...inputs[key] }
                if (PARTY_FIELDS.includes(key)) {
                  const overridden = !!String(overrides[key] || '').trim()
                  input.value = overridden ? overrides[key] : source[key] || ''
                  input.disabled = true
                  input.bgColor = overridden ? 'red-50' : ''
                  input.additionalElements = (
                    <span
                      className="mx-2 text-shade-blue cursor-pointer"
                      onClick={() =>
                        setOVData({
                          title: input.title,
                          overrideKey: key,
                          calcValue: source[key] || '',
                          ovValue: overrides[key] || '',
                          input: { ...input, disabled: false, additionalElements: null, readOnly: false, bgColor: '' },
                        })
                      }
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                  )
                }
                if (key === 'label1') input.additionalElements = rolodexButton('seller')
                if (key === 'label3') input.additionalElements = rolodexButton('lender')
                return (
                  <div
                    className={`input col-span-1 ${input.span && 'md:col-span-2 lg:col-span-3'} items-center`}
                    key={`item-${index}-${key}`}
                  >
                    <RenderInput input={input} Key={key} onChange={onChange} onBlur={onBlur} />
                  </div>
                )
              })}
            </div>
            <div className="">
              <div className="w-96 mx-auto">
                <Button full onClick={onGenerate}>
                  Generate Document
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {ovData.overrideKey && <OverrideCaclModal {...ovData} onClose={closeOVModal} />}
      {showGenerate && (
        <Modal
          isOpen
          title="Generate Document"
          titleOkay="Continue"
          onClose={() => setShowGenerate(false)}
          onOk={onConfirmGenerate}
        >
          <div className="w-full max-w-[560px] text-left">
            <RadioGroup
              id="escrow-generate-option"
              name="escrow-generate-option"
              title="Choose an action"
              options={GENERATE_OPTIONS}
              value={generateOption}
              onChange={setGenerateOption}
            />
          </div>
        </Modal>
      )}
      {rolodexFor && (
        <RolodexModal
          type={rolodexFor === 'seller' ? 'Builder or Seller' : 'Lender'}
          onClose={onCloseRolodex}
        />
      )}
    </div>
  )
}
